import { useContext, useEffect, useState } from 'react';
import { Checkbox } from 'baseui/checkbox';
import { Combobox } from 'baseui/combobox';
import { DatePicker } from 'baseui/datepicker';
import { Input } from 'baseui/input';
import { isFinite } from 'lodash';
import { DateTime } from 'luxon';
import Link from 'next/link';
import { UserPill } from '@gtintel/platform-atoms';
import { MARGIN_TARGETS, formatDate, getDesiredCost, getMargin, useDebounce } from '@gtintel/platform-utils';
import { ScrollResistantNumberInput } from '../../../components/Inputs';
import { MarginProgressBarRounded } from '../../../components/MarginProgress';
import { ProjectTags } from '../../../components/ProjectTags';
import SubprojectsList from '../../../components/SubprojectsList';
import { ProjectContext } from '../../context';
import styles from './styles.module.scss';
const parsePrice = price => {
  return isFinite(+price) ? +price : 0;
};
const dateToString = date => {
  return DateTime.fromJSDate(date).toFormat('yyyy-MM-dd');
};
const getOptionByLabel = (options, name) => {
  return options.find(option => option.name === name);
};
const getOptionById = (options, id) => {
  return options.find(option => option.id === id);
};
const getKeyByValue = (object, value) => {
  return Object.keys(object).find(key => object[key] === value);
};
const ProjectDetailsCard = () => {
  const ctx = useContext(ProjectContext);
  const debouncedEdit = useDebounce(ctx.edit, 500);
  const [isAnonymityRequested, setIsAnonymityRequested] = useState(ctx.requestedAnonymity());
  const [isQaRequired, setIsQaRequired] = useState(ctx.qaRequired());
  const [qaAssigned, setQaAssigned] = useState(ctx.qaAssigned());
  const qualityAssurers = ctx.qualityAssurers();
  const [finalPrice, setFinalPrice] = useState(ctx.clientPrice());
  const [netPrice, setNetPrice] = useState(parsePrice(finalPrice) - parsePrice(finalPrice) * ctx.discountPercentage());
  const discountPercentage = ctx.discountPercentage();
  const [projectStatus] = useState(ctx.status());
  const [cxLead, setCxLead] = useState(ctx.cxLead() ? ctx.cxLead().id : null);
  const [currency, setCurrency] = useState(ctx.currency());
  const [projectMargin, setProjectMargin] = useState(1);
  const id = ctx.projectId();
  const requestedCompletionDate = ctx.requestedCompletionDate();
  const cxLeads = ctx.cxLeads();
  const currencies = ctx.currencies();
  const dateRequestedCompletionDate = DateTime.fromISO(requestedCompletionDate);
  const npDueDate = ctx.npCompletionDate() ? DateTime.fromISO(ctx.npCompletionDate()).toJSDate() : dateRequestedCompletionDate.minus({
    days: 1
  }).toJSDate();
  const handlePriceChange = (setValue, value) => {
    setValue(parsePrice(value));
  };
  const handlePriceBlur = (key, value) => {
    ctx.edit({
      [key]: parsePrice(value)
    });
  };
  const setCurrencyFromCode = code => {
    const key = getKeyByValue(currencies, code);
    setCurrency(key);
    debouncedEdit({
      currency: key
    });
  };
  const isPriceFrozen = parseInt(projectStatus) > 3;
  // GTI Hub projects: the commercials sync from the Hub — edits belong there.
  const managedByHub = ctx.isManagedByHub();

  // Sync local finalPrice state with context when it changes (e.g., after template import)
  useEffect(() => {
    setFinalPrice(ctx.clientPrice());
  }, [ctx.clientPrice()]);
  useEffect(() => {
    setNetPrice(parsePrice(finalPrice) - parsePrice(finalPrice) * ctx.discountPercentage());
  }, [finalPrice, ctx.discountPercentage()]);
  useEffect(() => {
    setProjectMargin(getMargin(ctx.assignmentCosts() + parsePrice(ctx.qaCost()) + ctx.partnerAdjustmentsCost(), netPrice + ctx.clientAdjustmentsCost()));
  }, [netPrice, ctx.partnerAdjustmentsCost(), ctx.clientAdjustmentsCost()]);

  // Prevent number inputs from changing on scroll
  useEffect(() => {
    const handleWheel = e => {
      e.preventDefault();
    };
    const numberInputs = document.querySelectorAll('input[type="number"]');
    numberInputs.forEach(input => {
      input.addEventListener('wheel', handleWheel, {
        passive: false
      });
    });
    return () => {
      numberInputs.forEach(input => {
        input.removeEventListener('wheel', handleWheel);
      });
    };
  }, []);
  return <section className={styles.section}>
      <div className={styles.details}>
        <div className={styles.grid_row}>
          <div>
            <p>
              <span className={styles.h5}>Project ID: </span>#{id}
            </p>
            <p>
              <span className={styles.h5}>Project Type: </span>
              {ctx.projectType()}
            </p>
            {ctx.parent() && <p>
                <span className={styles.h5}>Parent Project: </span>
                <Link as={`/admin/project/detail/${ctx.parent().id}`} href="/admin/project/detail/[id]">
                  {ctx.parent().name}
                </Link>
              </p>}
            <p>
              <span className={styles.h5}>Created: </span>
              {formatDate(ctx.createdDate())}
            </p>
            <p>
              <span className={styles.h5}>Partner: </span>
              <UserPill user={ctx.partner()} showName hasBorder empty_message="No Partner Assigned" />
            </p>
            <p>
              <span className={styles.h5}>Notes for GTI Only: </span>
              {ctx.additionalInfo()}
            </p>

            {ctx.data().description && <p>
                <span className={styles.h5}>Description: </span>
                {ctx.data().description}
              </p>}
          </div>

          {ctx.subprojects() && ctx.subprojects().length > 0 && <SubprojectsList />}
        </div>

        <ProjectTags />

        {managedByHub && <p className={styles.hubManagedNotice} data-testid="hub-managed-notice">
            Managed in GTI Hub — price, currency, due date, CX lead, tags and
            billing flags are edited there and sync to Backoffice.
          </p>}

        <div className={styles.grid}>
          <div>
            <div className={styles.column}>
              <h5>Due Date</h5>
              <DatePicker id="due_date" disabled={managedByHub} formatString="dd/MM/yy" value={ctx.requestedCompletionDate() ? DateTime.fromISO(ctx.requestedCompletionDate()).toJSDate() : DateTime.now().toJSDate()} onChange={date => debouncedEdit({
              requested_completion_date: dateToString(date.date)
            })} clearable={true} aria-label="Due Date" overrides={{
              Input: {
                props: {
                  name: 'due_date'
                }
              }
            }} />
            </div>
            <div className={styles.row}>
              <Checkbox id="requested_anonymity" checked={isAnonymityRequested} onChange={e => {
              setIsAnonymityRequested(e.target.checked);
              ctx.edit({
                request_anonymity: e.target.checked
              });
            }} labelPlacement="right" overrides={{
              Label: {
                props: {
                  'data-testid': 'requested_anonymity'
                }
              },
              Input: {
                props: {
                  name: 'requested_anonymity'
                }
              }
            }}>
                Requested anonymity
              </Checkbox>

              <Checkbox id="qa_required" checked={isQaRequired} onChange={e => {
              setIsQaRequired(e.target.checked);
              if (e.target.checked === true) {
                ctx.edit({
                  qa_required: e.target.checked,
                  np_completion_date: dateToString(npDueDate)
                });
              } else {
                ctx.edit({
                  qa_required: e.target.checked
                });
              }
            }} labelPlacement="right" overrides={{
              Label: {
                props: {
                  'data-testid': 'qa_required'
                }
              },
              Input: {
                props: {
                  name: 'qa_required'
                }
              }
            }} title="If QA is required you should create a QA Assignment. These fields will be filled in based on that.">
                QA Required
              </Checkbox>
            </div>
            <div className={styles.column}>
              <h5>QA Cost:</h5>
              <ScrollResistantNumberInput id="qa_cost" value={ctx.qaCost()} startEnhancer={'£'} disabled={true} min={0} overrides={{
              Input: {
                props: {
                  name: 'qa_cost'
                }
              }
            }} />
            </div>
            <div className={styles.column}>
              <h5>QAer:</h5>
              <Combobox id="qa_assigned" value={qaAssigned !== null ? getOptionById(qualityAssurers, String(qaAssigned))?.name : null} onChange={nextValue => {
              const option = getOptionByLabel(qualityAssurers, nextValue);
              setQaAssigned(option ? Number(option.id) : null);
              debouncedEdit({
                qa_assigned: option ? Number(option.id) : nextValue
              });
            }} options={qualityAssurers} mapOptionToString={option => option.name} clearable={true} overrides={{
              Input: {
                props: {
                  name: 'qa_assigned'
                }
              }
            }} />
            </div>

            <div className={styles.column}>
              <h5>NP Completion Date: </h5>
              <DatePicker id="np_completion_date" formatString="dd/MM/yy" value={npDueDate} onChange={date => debouncedEdit({
              np_completion_date: dateToString(date.date)
            })} clearable={true} overrides={{
              Input: {
                props: {
                  name: 'np_completion_date'
                }
              }
            }} />
            </div>
          </div>

          <div>
            <div className={styles.column}>
              <h5>
                {parseInt(ctx.status()) > 3 ? 'Original ' : ''}Client Price:
              </h5>
              <ScrollResistantNumberInput id="client_price" disabled={isPriceFrozen || managedByHub} value={finalPrice} onChange={e => handlePriceChange(setFinalPrice, e.target.value)} onBlur={e => handlePriceBlur('final_price', e.target.value)} startEnhancer={currency} endEnhancer={<span className={styles.costHint}>
                    {currency}
                    {netPrice.toFixed(0)} after {discountPercentage * 100}%
                    discount
                  </span>} min={0} overrides={{
              Input: {
                props: {
                  name: 'client_price'
                }
              }
            }} />
            </div>
            <div className={styles.column}>
              <h5>Margin Targets:</h5>
              <div className={styles.marginCalculator}>
                {MARGIN_TARGETS.map(margin => <Input key={margin} value={currency + getDesiredCost(+netPrice, margin).toFixed(0)} readOnly positive startEnhancer={<span className={styles.marginTargetEnhancer}>
                        {(margin * 100).toFixed(0)}%
                      </span>} />)}
              </div>
            </div>
            <div className={styles.column}>
              <h5>
                {parseInt(ctx.status()) > 3 ? 'Original ' : ''}Assignment Costs
                & Margin:
              </h5>
              <ScrollResistantNumberInput id="assignment_costs" disabled={true} value={ctx.assignmentCosts()} startEnhancer={currency} endEnhancer={<MarginProgressBarRounded projectMargin={+projectMargin} />} min={0} />
            </div>

            <div className={styles.column}>
              <h5>Currency:</h5>
              <Combobox disabled={isPriceFrozen || managedByHub} value={currency !== null ? currencies[currency] : null} onChange={nextValue => {
              setCurrencyFromCode(nextValue);
            }} options={Object.values(currencies)} mapOptionToString={option => option} overrides={{
              Input: {
                props: {
                  name: 'currency'
                }
              }
            }} />
            </div>

            <div className={styles.column}>
              <h5>CX Lead</h5>
              <Combobox id="cx_lead" disabled={managedByHub} value={cxLead !== null ? getOptionById(cxLeads, String(cxLead))?.name : null} onChange={nextValue => {
              const option = getOptionByLabel(cxLeads, nextValue);
              setCxLead(option ? Number(option.id) : null);
              debouncedEdit({
                cx_lead: option ? Number(option.id) : nextValue
              });
            }} options={cxLeads} mapOptionToString={option => option.name} clearable={true} overrides={{
              Input: {
                props: {
                  name: 'cx_lead'
                }
              }
            }} />
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default ProjectDetailsCard;